import React, { useState } from 'react'

/**
 * Inline manager annotation zone — rendered inside an employee card.
 *
 * Rendering rules:
 * - Employee view, not shared OR no content → no DOM
 * - Employee view, shared, has content → read-only text
 * - Manager view, not ready → disabled textarea with helper
 * - Manager view, ready, not closed → editable textarea
 * - Manager view, closed, has content → read-only text
 * - Manager view, closed, no content → no DOM
 *
 * When `onSuggest` is provided and the note is editable, a sparkle
 * button appears that calls the AI suggestion endpoint.
 */
export function InlineManagerNote({
  value = '',
  onChange,
  onSuggest,
  isManager = false,
  isReady = false,
  isShared = false,
  isClosed = false,
  reportName = '',
  placeholder = 'What might they be missing? What pattern do you see? What would you ask them?',
}) {
  const [suggesting, setSuggesting] = useState(false)
  const hasContent = !!value?.trim()

  // Employee view: only show if shared AND has content
  if (!isManager) {
    if (!isShared || !hasContent) return null
    return (
      <>
        <div style={styles.separator} />
        <div style={styles.zone}>
          <span style={styles.label}>Manager note</span>
          <p style={styles.readOnlyText}>{value}</p>
        </div>
      </>
    )
  }

  // Manager view, closed: only show if has content
  if (isClosed) {
    if (!hasContent) return null
    return (
      <>
        <div style={styles.separator} />
        <div style={styles.zone}>
          <span style={styles.label}>Manager note</span>
          <p style={styles.readOnlyText}>{value}</p>
        </div>
      </>
    )
  }

  const canSuggest = isReady && !isClosed && !!onSuggest

  async function handleSuggest() {
    if (!onSuggest) return
    setSuggesting(true)
    try {
      const suggestion = await onSuggest()
      if (suggestion) onChange?.(suggestion)
    } finally {
      setSuggesting(false)
    }
  }

  // Manager view, not ready: disabled
  if (!isReady) {
    return (
      <>
        <div style={styles.separator} />
        <div style={styles.zone}>
          <span style={styles.label}>Manager note</span>
          <textarea
            style={{ ...styles.textarea, opacity: 0.5 }}
            disabled
            placeholder={
              reportName
                ? `You can add coaching notes once ${reportName} marks the reflection ready.`
                : 'You can add coaching notes once the reflection is marked ready.'
            }
            rows={2}
            aria-label="Manager note (disabled)"
          />
        </div>
      </>
    )
  }

  // Manager view, ready: editable
  return (
    <>
      <div style={styles.separator} />
      <div style={styles.zone}>
        <div style={styles.labelRow}>
          <span style={styles.label}>Manager note</span>
          {canSuggest && (
            <button
              type="button"
              onClick={handleSuggest}
              disabled={suggesting}
              style={styles.suggestBtn}
              aria-label="Suggest note with AI"
              title="Ask Claude to suggest a note"
            >
              {suggesting ? '...' : '✦ Suggest'}
            </button>
          )}
        </div>
        <textarea
          style={styles.textarea}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          rows={2}
          aria-label="Manager note"
        />
      </div>
    </>
  )
}

const styles = {
  separator: {
    height: '1px',
    background: 'var(--border)',
    margin: '12px 0',
  },
  zone: {
    background: 'var(--sidebar-bg)',
    borderRadius: '6px',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  readOnlyText: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: 1.6,
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  suggestBtn: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
}
